/**
 * GPU Spot-Bidding Optimizer (Patent Sub-system V)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, FlaskConical, Cpu, TrendingDown } from 'lucide-react';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUsd = (n) => `$${(n || 0).toFixed(2)}`;

const GpuSpot = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API_URL}/gpu-spot/demo`).then(r => setData(r.data)).finally(() => setLoading(false)); }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="gpu-spot-page">
      <Helmet><title>GPU Spot-Bidding Optimizer | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-yellow-300 border border-yellow-500/30 rounded-md px-2 py-1 mb-5" data-testid="gs-patent-badge">
            <FlaskConical className="w-3 h-3" /> Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>GPU Spot-Bidding</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">Vantage tracks spot interruption rates. <span className="text-yellow-300">We compute the bid that minimises $-per-training-step</span> given your checkpoint frequency.</p>
          <p className="mt-3 text-sm text-slate-500">Optimisation objective: bid × hours-until-interrupt × restart-overhead ÷ training-steps-completed.</p>
        </div>

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-12 text-slate-500" />}

        {data && (
          <>
            <Card className="mt-10 bg-gradient-to-br from-yellow-500/15 to-orange-500/5 border-yellow-500/30">
              <CardContent className="p-6 sm:p-8">
                <div className="text-xs uppercase tracking-widest text-yellow-300 font-semibold mb-2 flex items-center gap-2"><Cpu className="w-4 h-4" /> {data.instance}</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <div><div className="text-[10px] uppercase text-slate-500">On-demand</div><div className="text-2xl font-bold text-white" data-testid="gs-ondemand">{fmtUsd(data.on_demand_usd_per_hr)}/hr</div></div>
                  <div><div className="text-[10px] uppercase text-yellow-300">Optimal bid</div><div className="text-2xl font-bold text-yellow-300" data-testid="gs-optimal">{fmtUsd(data.optimal_bid_usd_per_hr)}/hr</div><div className="text-[10px] text-slate-500">{(data.optimal_bid_pct * 100).toFixed(0)}% of on-demand</div></div>
                  <div><div className="text-[10px] uppercase text-emerald-300">Savings</div><div className="text-2xl font-bold text-emerald-300" data-testid="gs-savings">{data.savings_vs_ondemand_pct}%</div><div className="text-[10px] text-slate-500">incl. interruption overhead</div></div>
                </div>
                <p className="text-xs text-slate-200 mt-5 leading-relaxed" data-testid="gs-rationale">{data.rationale}</p>
              </CardContent>
            </Card>

            <Card className="mt-6 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><TrendingDown className="w-5 h-5 text-yellow-300" /> Bid curve</CardTitle><CardDescription>Each row evaluates a candidate bid price. The optimum minimises $/training-step.</CardDescription></CardHeader>
              <CardContent>
                <table className="w-full text-sm" data-testid="gs-curve">
                  <thead className="text-[10px] uppercase tracking-widest text-slate-500"><tr className="border-b border-white/10"><th className="text-left p-2">Bid %</th><th className="text-right p-2">$/hr</th><th className="text-right p-2">Interrupt prob/hr</th><th className="text-right p-2">Hrs until interrupt</th><th className="text-right p-2">Overhead %</th><th className="text-right p-2">$/step</th></tr></thead>
                  <tbody>
                    {data.bid_points.map((p, i) => {
                      const isOpt = p.bid_pct_of_ondemand === data.optimal_bid_pct;
                      return (
                        <tr key={i} className={`border-b border-white/5 ${isOpt ? 'bg-yellow-500/10' : 'hover:bg-slate-950/60'}`}>
                          <td className={`p-2 text-xs font-mono ${isOpt ? 'text-yellow-300 font-semibold' : 'text-slate-200'}`}>{(p.bid_pct_of_ondemand * 100).toFixed(0)}%</td>
                          <td className="p-2 text-xs text-right text-slate-200 font-mono">{fmtUsd(p.bid_usd_per_hr)}</td>
                          <td className="p-2 text-xs text-right text-amber-300 font-mono">{(p.interruption_prob_per_hour * 100).toFixed(2)}%</td>
                          <td className="p-2 text-xs text-right text-slate-300 font-mono">{p.expected_hours_until_interrupt.toFixed(1)}h</td>
                          <td className="p-2 text-xs text-right text-rose-300 font-mono">{p.restart_overhead_pct}%</td>
                          <td className={`p-2 text-xs text-right font-mono font-semibold ${isOpt ? 'text-yellow-300' : 'text-white'}`}>${p.effective_cost_per_step_usd.toFixed(5)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default GpuSpot;
